
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardFooter, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDate } from './utils';
import { AlertIcon, DocumentIcon } from '../BorrowingsIcons';

interface LoanEventsProps {
  events: any[];
  onApprove?: (eventId: number) => void;
}

export const LoanEvents: React.FC<LoanEventsProps> = ({ events, onApprove }) => {
  if (!events || events.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8">
            <DocumentIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium">No Events Recorded</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Events like interest rate changes, covenant breaches, etc. will appear here
            </p>
            <Button className="mt-4" size="sm">Record Event</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Loan Events History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {events.map((event) => (
            <Card key={event.id} className="overflow-hidden">
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertIcon className="h-4 w-4" />
                    <span className="font-medium">{getEventTypeLabel(event.type)}</span>
                  </div>
                  <Badge 
                    variant={
                      event.status === 'Approved' ? 'outline' :
                      event.status === 'Pending Approval' ? 'secondary' : 
                      'default'
                    }
                  >
                    {event.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="px-4 py-2">
                <div className="text-sm text-muted-foreground mb-2">
                  Date: {formatDate(event.date)}
                </div>
                
                {event.type === 'interest_rate' && (
                  <div className="text-sm">
                    <div>Previous Rate: {event.details.previousRate}%</div>
                    <div>New Rate: {event.details.newRate}%</div>
                    {event.details.benchmarkName && event.details.benchmarkName !== 'Not specified' && (
                      <div>Benchmark: {event.details.benchmarkName}</div>
                    )}
                    {event.details.reason && (
                      <div>Reason: {event.details.reason}</div>
                    )}
                  </div>
                )}
                
                {event.type === 'prepayment' && (
                  <div className="text-sm">
                    <div>Amount: ₹{event.details.amount.toLocaleString()}</div>
                    <div>Date: {formatDate(event.details.date)}</div>
                    <div>Type: {event.details.type}</div>
                  </div>
                )}
                
                {event.type === 'moratorium' && (
                  <div className="text-sm">
                    <div>Start Date: {formatDate(event.details.startDate)}</div>
                    <div>End Date: {formatDate(event.details.endDate)}</div>
                    <div>Interest: {event.details.deferInterest ? 'Deferred' : 'Accrued'}</div>
                  </div>
                )}
              </CardContent>
              
              {event.status === 'Pending Approval' && onApprove && (
                <CardFooter className="border-t px-4 py-2 bg-muted/50 flex justify-end">
                  <Button 
                    size="sm" 
                    onClick={() => onApprove(event.id)}
                  >
                    Approve
                  </Button>
                </CardFooter>
              )}
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Helper function to get a human-readable label for event types
const getEventTypeLabel = (eventType: string) => {
  switch (eventType) {
    case 'interest_rate':
      return 'Interest Rate Change';
    case 'prepayment':
      return 'Prepayment';
    case 'moratorium':
      return 'Moratorium';
    default:
      return 'Loan Event';
  }
};
